import { FC, ReactNode, useCallback, useMemo, useEffect, useState } from "react"
import { Link, PageProps, withPrefix, Script } from "gatsby"
import classNames from "classnames"
import { Button, Typography } from "@eventbrite/marmalade"
import { useDispatch, useSelector } from "react-redux"

// components
import { Banner } from "../Banner/Banner"
import { ChatBot } from "../ChatBot/ChatBot"
import { Footer, FooterCopy } from "../Footer/Footer"
import { FooterNavCopy } from "../Footer/FooterBase"
import { Header } from "../Header/Header"
import { MicroCopy } from "../ContactForm/ContactForm"
import { NiceCXOneScript } from "../../components/ChatWidget/NiceCXOneScript"

// utils
import { events } from "../../utils/heap/tracking_events"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { handleUrlString } from "../../utils/seo/seo_utils"
import {
  extractLangCode,
  extractLangCodeFromString,
  isDateWithin24Hours,
  isLiveSupportedLocale,
  isStringValidDate
} from "../../utils/general_utils"

// styling
import * as styles from "./Layout.module.scss"
import {
  questionsButton,
  questionsTitle,
  stillHaveQuestionsContainer,
  questionsWrapper
} from "../../pages/{ContentfulHelpArticle.node_locale}/index.module.scss"

// types
import { filterOrderData } from "../../utils/filter_data_utils"
import { getBaseUrl } from "../../utils/general_utils"
import { RootState } from "../../store/store"
import { UserProfile } from "../../store/slices/user_profile.slice"
import { EventbriteOrder, SingleEvent } from "../../utils/auth/enrich_user_data"

// data
import {
  FALLBACK_MICROCOPY,
  FALLBACK_FOOTERCOPY,
  FALLBACK_FOOTERNAVCOPY
} from "./layout-fallback-data"
import { setMetaData } from "../../store/slices/metadata.slice"
import { getFlag } from "../../utils/query_param_flags"

export type PickedMicroCopy = Pick<
  Queries.ContentfulMicrocopy,
  "microcopyKey" | "microcopyValue"
>

export type LayoutMicroCopy = {
  messageSearchBar: Queries.HomepageDataQuery["messageSearchBar"]
  headerSearchResults: Queries.HomepageDataQuery["headerSearchResults"]
  buttonContactUs: Queries.HomepageDataQuery["buttonContactUs"]
  buttonBrowseHelpCenter: Queries.HomepageDataQuery["buttonBrowseHelpCenter"]
  messageContactUs: Queries.HomepageDataQuery["messageContactUs"]
  upperNavMicroCopySet: Queries.HomepageDataQuery["upperNavMicroCopySet"]
  chatbotUITranslations: Queries.HomepageDataQuery["chatbotUITranslations"]
}

export type Props = {
  children: ReactNode
  microCopy: LayoutMicroCopy
  footerCopy: FooterCopy
  footerNavCopy: FooterNavCopy
  gatsbyPageProps: PageProps
  className?: string
  contact?: boolean
  page?: string
  searchBar?: {
    isSearchBarVisible: boolean
    isResultsBoxShown: boolean
  }
}

type ButtonCopy = {
  buttonContactUs: MicroCopy
  buttonBrowseHelpCenter: MicroCopy
}

export const Layout: FC<Props> = ({
  children,
  className,
  gatsbyPageProps,
  microCopy = FALLBACK_MICROCOPY,
  footerCopy = FALLBACK_FOOTERCOPY,
  footerNavCopy = FALLBACK_FOOTERNAVCOPY,
  contact = true,
  page = "Contact Us",
  searchBar = {
    isSearchBarVisible: false,
    isResultsBoxShown: true
  }
}) => {
  const transcendAirgapUrl = process.env.GATSBY_TRANSCEND_SCRIPT_URL || ""

  const prefixedBasePath = handleUrlString(
    withPrefix(`/${gatsbyPageProps?.pageContext["node_locale"]}`?.toLowerCase())
  )

  const dispatch = useDispatch()

  const urlParamFlag = getFlag()

  const showLiveChatGlobal = useSelector(
    (state: RootState) => state?.metaData?.showLiveChatGlobal
  )
  const useGenesysChat = useSelector(
    (state: RootState) => state?.metaData?.useGenesysChat
  )

  // gather data needed for Heap event checking for 24hr event status
  const userProfile = useSelector(
    (State: RootState) => State.userProfile
  ) as UserProfile | null
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )
  const langCode = gatsbyPageProps?.path
    ? extractLangCodeFromString(gatsbyPageProps.path)
    : extractLangCode()
  const liveSupportedLocale = isLiveSupportedLocale(
    [
      "en-us",
      "en-au",
      "en-gb",
      "en-hk",
      "en-ie",
      "en-sg",
      "en-nz",
      "en-ca",
      "sv",
      "da",
      "fi",
      "pt-br"
    ],
    langCode
  )
  const isPaid = !userProfile?.isFree
  const isAttendee = !userProfile?.isOrganizer

  const orders = useMemo<EventbriteOrder[] | []>(() => {
    if (userProfile && userProfile?.orders && userProfile?.orders?.length > 0) {
      return userProfile.orders
    }

    return []
  }, [userProfile])
  const ebEvents = useMemo<SingleEvent[] | []>(() => {
    if (orders?.length > 0) {
      return orders
        .map((order) => {
          if (order?.event) {
            return order.event
          }

          return null
        })
        .filter((order) => order !== null)
    }

    return []
  }, [orders])
  const hasEventWithin24Hours = useMemo<boolean>(() => {
    if (ebEvents?.length > 0) {
      return ebEvents.some((ebEvent) => {
        if (
          ebEvent?.start?.utc?.length > 0 &&
          isStringValidDate(ebEvent.start.utc)
        ) {
          return isDateWithin24Hours(new Date(ebEvent.start.utc))
        }

        return false
      })
    }

    return false
  }, [ebEvents])
  const [hasFiredEventWithin24HrsEvent, setHasFiredEventWithin24HrsEvent] =
    useState(false)

  if (typeof window !== "undefined") {
    useEffect(() => {
      const params = new URLSearchParams(window.location.search)
      const paramValue = params.get("genesysChat")

      if (
        (paramValue && paramValue?.toLowerCase() === "true") ||
        paramValue?.toLowerCase() === "false"
      ) {
        dispatch(
          setMetaData({
            useGenesysChat: true
          })
        )
      }
    }, [])
  }

  useEffect(() => {
    if (hasEventWithin24Hours && !hasFiredEventWithin24HrsEvent) {
      events.userHasEventWithin24Hours(
        hasEventWithin24Hours,
        isAttendee,
        Layout.name
      )
      setHasFiredEventWithin24HrsEvent(true)
    }
  }, [hasEventWithin24Hours, ebEvents, hasFiredEventWithin24HrsEvent])

  useEffect(() => {
    if (typeof window === "undefined") return
    let quarters = 0

    const onScroll = function () {
      if (typeof window !== "undefined") {
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight
        const quarterHeight = scrollHeight / 4
        const scrollDistance =
          window.pageYOffset ||
          (document.documentElement || document.body).scrollTop ||
          0
        const divisible = Math.trunc(scrollDistance / quarterHeight)
        if (quarters < divisible && divisible !== Infinity) {
          const scrollPercent = Math.min(divisible * 25, 100)
          events.scrollDepth(scrollPercent)
          quarters++
        }
      }
    }

    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  // setup callback to send order data to decagon
  if (typeof window !== "undefined") {
    useEffect(() => {
      // find my tickets
      if (window?.duet?.setMetadataRequestListener && fetch) {
        window.duet.setMetadataRequestListener(async (payload, callback) => {
          const { subtype } = payload

          if (subtype?.toLowerCase() == "orders") {
            try {
              const response = await fetch(
                `${getBaseUrl()}/api/v3/users/me/orders?time_filter=current_future&expand=event,event.refund_policy`,
                {
                  method: "GET",
                  credentials: "include"
                }
              )

              if (!response.ok) {
                callback({ ok: false, status: response.status })
                events.findMyTicketsError(await response.text())
              } else {
                const data = await response.json()

                callback({
                  ok: true,
                  orders:
                    data.orders?.length > 0 ? filterOrderData(data.orders) : []
                })
              }
            } catch (error) {
              // @ts-ignore
              if (
                typeof error === "object" &&
                // @ts-ignore
                typeof error?.message === "string"
              ) {
                // @ts-ignore
                events.findMyTicketsError(error?.message)
              }
            }
          }
        })
      }
    }, [window?.duet])
  }

  const buttonCopy = useMemo<ButtonCopy>(() => {
    return {
      buttonBrowseHelpCenter: {
        raw: microCopy?.buttonBrowseHelpCenter?.microcopyValue?.raw || ""
      },
      buttonContactUs: {
        raw: microCopy?.buttonContactUs?.microcopyValue?.raw || ""
      }
    }
  }, [microCopy])

  const handleContactButtonClick = useCallback((page: string) => {
    if (page === "Contact Us") {
      events?.layoutContactUsButtonClick(Layout?.name)
    }
  }, [])

  return (
    <main
      className={classNames(styles?.layoutContainer, className)}
      role="main"
    >
      <Header
        urlPath={prefixedBasePath}
        isResultsBoxShown={searchBar?.isResultsBoxShown}
        isSearchBarVisible={searchBar?.isSearchBarVisible}
        headerMicroCopy={microCopy.upperNavMicroCopySet}
        headerSiteSearchMicroCopy={{
          headerSearchResults: microCopy?.headerSearchResults,
          messageSearchBar: microCopy?.messageSearchBar
        }}
      />

      <div className={styles?.outerWrapper}>
        <Banner />
        <div className={styles?.contentWrap}>{children}</div>
      </div>

      {contact ? (
        <div className={stillHaveQuestionsContainer}>
          <div className={questionsWrapper}>
            <h3 className={questionsTitle}>
              {renderRichText(
                microCopy?.messageContactUs?.microcopyValue?.raw,
                customRendererToRemovePTag
              )}
            </h3>
            <Link
              to={
                page === "Contact Us"
                  ? handleUrlString(`${prefixedBasePath}/contact-us`, {
                      removePrefix: true
                    })
                  : handleUrlString(prefixedBasePath, { removePrefix: true })
              }
              onClick={() => handleContactButtonClick(page)}
            >
              <Button
                size="responsive"
                variant="primary"
                className={questionsButton}
              >
                <Typography
                  variant="heading-xs"
                  style={{
                    fontWeight: 600
                  }}
                >
                  {page === "Contact Us"
                    ? renderRichText(
                        buttonCopy?.buttonContactUs?.raw,
                        customRendererToRemovePTag
                      )
                    : renderRichText(
                        buttonCopy?.buttonBrowseHelpCenter?.raw,
                        customRendererToRemovePTag
                      )}
                </Typography>
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      <Footer footerCopy={footerCopy} footerNavCopy={footerNavCopy} />
      <ChatBot chatbotUITranslations={microCopy.chatbotUITranslations} />
      {showLiveChatGlobal && !useGenesysChat ? (
        <NiceCXOneScript dispatch={dispatch} />
      ) : null}
    </main>
  )
}
